// User Management Component
// Admin-only interface for creating and managing users

import React, { useState, useEffect } from 'react';
import { userAPI, flashReportAPI } from '../utils/api';
import '../styles/UserManagement.css';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        full_name: '',
        email: '',
        is_admin: false,
        is_active: true,
        default_plant_id: '',
        permissions: []
    });

    // Load users and plants when component mounts
    useEffect(() => {
        loadUsers();
        loadPlants();
    }, []);

    // Load all users
    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await userAPI.getAllUsers();
            setUsers(data.users);
        } catch (error) {
            setMessage({ text: 'Error loading users', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Load all plants
    const loadPlants = async () => {
        try {
            const data = await flashReportAPI.getPlants();
            setPlants(data.plants);
        } catch (error) {
            setMessage({ text: 'Error loading plants', type: 'error' });
        }
    };

    // Open modal for creating new user
    const handleCreateNew = () => {
        setEditingUser(null);
        setFormData({
            username: '',
            password: '',
            full_name: '',
            email: '',
            is_admin: false,
            is_active: true,
            default_plant_id: '',
            permissions: []
        });
        setShowModal(true);
        setMessage({ text: '', type: '' });
    };

    // Open modal for editing existing user
        const handleEdit = (user) => {
            console.log('🔍 Editing user:', user);
            console.log('🔍 User permissions:', user.permissions);
            
            const mappedPermissions = user.permissions.map(p => ({
                plant_id: p.plant_id,
                permission_level: p.permission_level,
                flash_report_reminders: Boolean(p.flash_report_reminders),
                utilities_reminders: Boolean(p.utilities_reminders)
            }));
            
            console.log('🔍 Mapped permissions:', mappedPermissions);
            
            setEditingUser(user);
            setFormData({
                username: user.username,
                password: '', // Don't populate password for security
                full_name: user.full_name,
                email: user.email || '',
                is_admin: Boolean(user.is_admin),
                is_active: Boolean(user.is_active),
                default_plant_id: user.default_plant_id || '',
                permissions: mappedPermissions
            });
            setShowModal(true);
            setMessage({ text: '', type: '' });
        };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle permission changes
        const handlePermissionChange = (plantId, permissionLevel, flashReminder, utilType, utilReminder) => {
        setFormData(prev => {
            const existingIndex = prev.permissions.findIndex(p => p.plant_id === plantId);
            const newPermissions = [...prev.permissions];

            // Changing permission level (view/edit/none)
            if (permissionLevel !== undefined && permissionLevel !== null && !utilType) {
                if (permissionLevel === 'none') {
                    // Remove permission
                    if (existingIndex !== -1) {
                        newPermissions.splice(existingIndex, 1);
                    }
                } else {
                    // Add or update permission level
                    if (existingIndex !== -1) {
                        newPermissions[existingIndex].permission_level = permissionLevel;
                    } else {
                        newPermissions.push({ 
                            plant_id: plantId, 
                            permission_level: permissionLevel,
                            flash_report_reminders: false,
                            utilities_reminders: false
                        });
                    }
                }
            }
            // Changing flash report reminder checkbox
            else if (utilType === 'flash') {
                if (existingIndex !== -1) {
                    newPermissions[existingIndex].flash_report_reminders = flashReminder;
                }
            }
            // Changing utilities reminder checkbox
            else if (utilType === 'utilities') {
                if (existingIndex !== -1) {
                    newPermissions[existingIndex].utilities_reminders = utilReminder;
                }
            }

            return { ...prev, permissions: newPermissions };
        });
    };

    // Get permission level for a plant
    const getPermissionLevel = (plantId) => {
        const perm = formData.permissions.find(p => p.plant_id === plantId);
        return perm ? perm.permission_level : 'none';
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.username || !formData.full_name) {
            setMessage({ text: 'Username and full name are required', type: 'error' });
            return;
        }

        if (!editingUser && !formData.password) {
            setMessage({ text: 'Password is required for new users', type: 'error' });
            return;
        }

        try {
            setLoading(true);

            if (editingUser) {
                // Update existing user
                await userAPI.updateUser(editingUser.user_id, formData);
                setMessage({ text: 'User updated successfully', type: 'success' });
            } else {
                // Create new user
                await userAPI.createUser(formData);
                setMessage({ text: 'User created successfully', type: 'success' });
            }

            // Reload users and close modal
            await loadUsers();
            setShowModal(false);
            
            // Clear message after 3 seconds
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error saving user';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Handle user deactivation
    const handleDeactivate = async (userId) => {
        if (!window.confirm('Are you sure you want to deactivate this user?')) {
            return;
        }

        try {
            setLoading(true);
            await userAPI.deleteUser(userId);
            setMessage({ text: 'User deactivated successfully', type: 'success' });
            await loadUsers();
            
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error deactivating user';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-management-container">
            <div className="user-management-header">
                <h2>User Management</h2>
                <button onClick={handleCreateNew} className="btn-create" disabled={loading}>
                    + Create New User
                </button>
            </div>

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            {loading && !showModal ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading users...</p>
                </div>
            ) : (
                <div className="users-table-container">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Full Name</th>
                                <th>Email</th>
                                <th>Admin</th>
                                <th>Status</th>
                                <th>Plant Access</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.user_id}>
                                    <td>{user.username}</td>
                                    <td>{user.full_name}</td>
                                    <td>{user.email || '-'}</td>
                                    <td>
                                        {user.is_admin ? (
                                            <span className="badge badge-admin">Admin</span>
                                        ) : (
                                            <span className="badge badge-user">User</span>
                                        )}
                                    </td>
                                    <td>
                                        {user.is_active ? (
                                            <span className="badge badge-active">Active</span>
                                        ) : (
                                            <span className="badge badge-inactive">Inactive</span>
                                        )}
                                    </td>
                                    <td>
                                        {user.is_admin ? (
                                            <span className="plant-access-all">All Plants</span>
                                        ) : user.permissions.length > 0 ? (
                                            <div className="plant-access-list">
                                                {user.permissions.map(perm => (
                                                    <span key={perm.plant_id} className="plant-badge">
                                                        {perm.plant_name} ({perm.permission_level})
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="plant-access-none">No access</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button 
                                                onClick={() => handleEdit(user)}
                                                className="btn-edit"
                                                disabled={loading}
                                            >
                                                Edit
                                            </button>
                                            {user.is_active && (
                                                <button 
                                                    onClick={() => handleDeactivate(user.user_id)}
                                                    className="btn-deactivate"
                                                    disabled={loading}
                                                >
                                                    Deactivate
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal for Create/Edit User */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingUser ? 'Edit User' : 'Create New User'}</h3>
                            <button onClick={() => setShowModal(false)} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="user-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="username">Username *</label>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="full_name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="full_name"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="email">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password">
                                        Password {!editingUser && '*'}
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required={!editingUser}
                                        placeholder={editingUser ? 'Leave blank to keep current' : ''}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="default_plant_id">Default Plant (Optional)</label>
                                    <select
                                        id="default_plant_id"
                                        name="default_plant_id"
                                        value={formData.default_plant_id}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                    >
                                        <option value="">No default plant</option>
                                        {plants.map(plant => (
                                            <option key={plant.plant_id} value={plant.plant_id}>
                                                {plant.plant_name}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="field-hint">This plant will be pre-selected on data entry forms</small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group-checkbox">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_admin"
                                            checked={formData.is_admin}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        <span>Admin User (has access to all plants)</span>
                                    </label>
                                </div>

                                <div className="form-group-checkbox">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        <span>Active</span>
                                    </label>
                                </div>
                            </div>

                            {!formData.is_admin && (
                                <div className="permissions-section">
                                    <h4>Plant Permissions</h4>
                                    <p className="permissions-hint">
                                        Select permission level for each plant this user should access
                                    </p>
                                    
                                    <div className="permissions-grid">
                                        {plants.map(plant => (
                                            <div className="permission-item">
                                                <div className="permission-header">
                                                    <span className="plant-name">{plant.plant_name}</span>
                                                    <select
                                                        value={getPermissionLevel(plant.plant_id)}
                                                        onChange={(e) => handlePermissionChange(plant.plant_id, e.target.value)}
                                                        disabled={loading}
                                                    >
                                                        <option value="none">No Access</option>
                                                        <option value="view">View Only</option>
                                                        <option value="edit">Edit</option>
                                                    </select>
                                                </div>
                                                {getPermissionLevel(plant.plant_id) !== 'none' && (
                                                    <div className="reminder-checkboxes">
                                                        <label className="reminder-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.permissions.find(p => p.plant_id === plant.plant_id)?.flash_report_reminders || false}
                                                                onChange={(e) => {
                                                                    const perm = formData.permissions.find(p => p.plant_id === plant.plant_id);
                                                                    if (perm) {
                                                                        handlePermissionChange(plant.plant_id, perm.permission_level, e.target.checked, 'flash');
                                                                    }
                                                                }}
                                                                disabled={loading}
                                                            />
                                                            <span>Flash Report Reminders</span>
                                                        </label>
                                                        <label className="reminder-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.permissions.find(p => p.plant_id === plant.plant_id)?.utilities_reminders || false}
                                                                onChange={(e) => {
                                                                    const perm = formData.permissions.find(p => p.plant_id === plant.plant_id);
                                                                    if (perm) {
                                                                        handlePermissionChange(plant.plant_id, perm.permission_level, null, 'utilities', e.target.checked);
                                                                    }
                                                                }}
                                                                disabled={loading}
                                                            />
                                                            <span>Utilities Reminders</span>
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)}
                                    className="btn-cancel"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-save"
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserManagement;